import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Plus, Pencil, Trash2, Search, ChevronDown, ChevronRight, Sparkles, Store, UtensilsCrossed, Pill, ShoppingCart, Briefcase, Check, Loader2, Eraser, CornerDownRight } from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';

const ACCOUNT_TYPES = [
  { value: 'asset', label: 'Asset', color: 'bg-info/10 text-info border-info/30', dot: 'bg-info' },
  { value: 'liability', label: 'Liability', color: 'bg-error/10 text-error border-error/30', dot: 'bg-error' },
  { value: 'equity', label: 'Equity', color: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  { value: 'revenue', label: 'Revenue', color: 'bg-success/10 text-success border-success/30', dot: 'bg-success' },
  { value: 'expense', label: 'Expense', color: 'bg-warning/10 text-warning border-warning/30', dot: 'bg-warning' },
];

const SUB_TYPES = {
  asset: ['current_asset', 'fixed_asset', 'other_asset'],
  liability: ['current_liability', 'long_term_liability'],
  equity: ['equity'],
  revenue: ['operating_revenue', 'other_revenue'],
  expense: ['cost_of_sales', 'operating_expense', 'other_expense'],
};

const BUSINESS_TYPE_ICONS = {
  restaurant: UtensilsCrossed,
  retail: ShoppingCart,
  pharmacy: Pill,
  trading: Store,
  services: Briefcase,
};

const BUSINESS_TYPE_COLORS = {
  restaurant: 'from-orange-500 to-red-500',
  retail: 'from-blue-500 to-indigo-500',
  pharmacy: 'from-emerald-500 to-teal-500',
  trading: 'from-amber-500 to-yellow-500',
  services: 'from-violet-500 to-purple-500',
};

export default function ChartOfAccountsPage() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editAcc, setEditAcc] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', type: 'asset', sub_type: 'current_asset', description: '' });
  const [expandedTypes, setExpandedTypes] = useState(['asset', 'liability', 'equity', 'revenue', 'expense']);
  const [categoryTree, setCategoryTree] = useState([]);

  // Business type setup
  const [businessTypes, setBusinessTypes] = useState([]);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [selectedBizType, setSelectedBizType] = useState('');
  const [settingUp, setSettingUp] = useState(false);
  const [setupDone, setSetupDone] = useState(false);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/accounting/accounts');
      setAccounts(res.data);
    } catch { toast.error('Failed to load accounts'); }
  };

  const fetchCategoryTree = async () => {
    try {
      const r = await api.get('/accounting/expense-category-tree');
      setCategoryTree(r.data?.tree || []);
    } catch { /* non-fatal */ }
  };

  const fetchBusinessTypes = async () => {
    try {
      const res = await api.get('/accounting/business-types');
      setBusinessTypes(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchAccounts();
    fetchBusinessTypes();
    fetchCategoryTree();
  }, []);

  const handleSetupBizType = async () => {
    if (!selectedBizType) { toast.error('Please select a business type'); return; }
    setSettingUp(true);
    try {
      const res = await api.post('/accounting/setup-business-type', { business_type: selectedBizType });
      toast.success(`Added ${res.data.accounts_added} accounts and ${res.data.categories_added} expense categories for ${selectedBizType}`);
      setSetupDone(true);
      fetchAccounts();
      setTimeout(() => { setShowSetupWizard(false); setSetupDone(false); }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setup failed');
    }
    setSettingUp(false);
  };

  const handleSave = async () => {
    if (!form.code || !form.name) { toast.error('Code and name are required'); return; }
    try {
      if (editAcc) {
        await api.put(`/accounting/accounts/${editAcc.id}`, form);
        toast.success('Account updated');
      } else {
        await api.post('/accounting/accounts', form);
        toast.success('Account created');
      }
      setShowModal(false);
      setEditAcc(null);
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (acc) => {
    if (acc.is_system) { toast.error('Cannot delete system accounts'); return; }
    if (!window.confirm(`Delete account "${acc.name}"?`)) return;
    try {
      await api.delete(`/accounting/accounts/${acc.id}`);
      toast.success('Account deleted');
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openNew = () => {
    setEditAcc(null);
    setForm({ code: '', name: '', name_ar: '', type: 'asset', sub_type: 'current_asset', parent_code: '', description: '' });
    setShowModal(true);
  };

  const openAddSub = async (parent) => {
    // Pre-fill parent + auto-fetch next dash-numbered code
    setEditAcc(null);
    setForm({
      code: '', name: '', name_ar: '',
      type: parent.type,
      sub_type: parent.sub_type || (SUB_TYPES[parent.type] || [''])[0],
      parent_code: parent.code,
      description: '',
    });
    setShowModal(true);
    try {
      const res = await api.get('/accounting/accounts/next-code', {
        params: { parent_code: parent.code },
      });
      setForm(f => ({ ...f, code: res.data?.next_code || '' }));
    } catch { /* non-fatal */ }
  };

  const handleDedupe = async () => {
    try {
      const preview = await api.get('/accounting/accounts/duplicates');
      const accGroups = preview.data?.account_duplicate_groups || 0;
      const catGroups = preview.data?.category_duplicate_groups || 0;
      if (!accGroups && !catGroups) {
        toast.success('No duplicates found — your chart is clean!');
        return;
      }
      if (!window.confirm(
        `Found ${accGroups} duplicate account group(s) and ${catGroups} duplicate category group(s).\n\nThis will KEEP the newest copy, fill missing fields from older copies, re-point journal lines, and delete the older duplicates.\n\nProceed?`,
      )) return;
      const res = await api.post('/accounting/accounts/dedupe');
      const d = res.data || {};
      toast.success(`Merged ${d.accounts_merged || 0} accounts + ${d.categories_merged || 0} categories · repointed ${d.journal_line_refs_repointed || 0} JE lines`);
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dedupe failed');
    }
  };

  const openEdit = (acc) => {
    setEditAcc(acc);
    setForm({ code: acc.code, name: acc.name, name_ar: acc.name_ar || '', type: acc.type, sub_type: acc.sub_type || '', parent_code: acc.parent_code || '', description: acc.description || '' });
    setShowModal(true);
  };

  const toggleType = (type) => {
    setExpandedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const filtered = accounts.filter(a => {
    if (filterType !== 'all' && a.type !== filterType) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) && !a.code.includes(search)) return false;
    return true;
  });

  const groupedByType = ACCOUNT_TYPES.map(type => ({
    ...type,
    accounts: filtered.filter(a => a.type === type.value),
    total: filtered.filter(a => a.type === type.value).reduce((s, a) => s + (a.balance || 0), 0),
  })).filter(g => g.accounts.length > 0);

  // Build a depth map so we can indent accounts inside their parent chain.
  // Walks the parent_code chain up to 5 levels (safety cap).
  const depthByCode = (() => {
    const byCode = {};
    accounts.forEach(a => { byCode[a.code] = a; });
    const cache = {};
    const compute = (code, visited = new Set()) => {
      if (cache[code] !== undefined) return cache[code];
      if (visited.has(code)) return 0;
      visited.add(code);
      const node = byCode[code];
      if (!node || !node.parent_code) { cache[code] = 0; return 0; }
      const d = 1 + compute(node.parent_code, visited);
      cache[code] = Math.min(d, 5);
      return cache[code];
    };
    accounts.forEach(a => compute(a.code));
    return cache;
  })();

  // Sort accounts inside each group by parent_code chain so children appear
  // under their parents in the natural tree order (code-sorted within siblings).
  const orderedTree = (items) => {
    const byParent = {};
    items.forEach(a => {
      const p = a.parent_code || '__root__';
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(a);
    });
    Object.values(byParent).forEach(arr => arr.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })));
    const out = [];
    const walk = (parent) => {
      (byParent[parent] || []).forEach(n => {
        out.push(n);
        walk(n.code);
      });
    };
    walk('__root__');
    // Any remaining orphans (parent_code points outside the filtered set)
    const seen = new Set(out.map(x => x.code));
    items.forEach(a => { if (!seen.has(a.code)) out.push(a); });
    return out;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="chart-of-accounts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-200/50">
              <BookOpen size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Chart of Accounts</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t('coa_subtitle') || 'Manage your accounting structure'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/category-account-mapping'} size="sm" data-testid="category-mapping-btn" className="text-xs">
              <BookOpen className="w-4 h-4 mr-1" /> Category Mapping
            </Button>
            <Button variant="outline" onClick={handleDedupe} size="sm" data-testid="dedupe-btn" className="text-xs" title="Remove duplicate accounts (keep newest, merge old into new)">
              <Eraser className="w-4 h-4 mr-1" /> Dedupe
            </Button>
            <Button variant="outline" onClick={async () => {
              if (!window.confirm('Reset to default chart of accounts? This will replace all system accounts with the comprehensive defaults.')) return;
              try {
                const res = await api.post('/accounting/accounts/reset-defaults');
                setAccounts(res.data);
                toast.success('Chart of Accounts reset to defaults');
              } catch (e) { toast.error('Reset failed'); }
            }} size="sm" data-testid="reset-defaults-btn" className="text-xs">
              Reset Defaults
            </Button>
            <Button variant="outline" onClick={() => { setShowSetupWizard(true); setSelectedBizType(''); setSetupDone(false); }} size="sm" data-testid="setup-business-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Setup by Business
            </Button>
            <Button onClick={openNew} size="sm" data-testid="add-account-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Account
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {ACCOUNT_TYPES.map(type => {
            const typeAccounts = accounts.filter(a => a.type === type.value);
            return (
              <Card key={type.value} className={`border ${type.color.split(' ').find(c => c.startsWith('border-'))}`} data-testid={`summary-${type.value}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${type.dot}`} />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{type.label}</p>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{typeAccounts.length}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">accounts</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search by name or code..." value={search}
                  onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-accounts" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === 'all' ? 'bg-muted text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  data-testid="filter-all">All</button>
                {ACCOUNT_TYPES.map(tp => (
                  <button key={tp.value} onClick={() => setFilterType(tp.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === tp.value ? 'bg-muted text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                    data-testid={`filter-${tp.value}`}>{tp.label}</button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Groups */}
        <div className="space-y-3">
          {groupedByType.map(group => (
            <Card key={group.value} className="overflow-hidden" data-testid={`group-${group.value}`}>
              <button onClick={() => toggleType(group.value)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  {expandedTypes.includes(group.value) ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  <Badge className={`text-[10px] ${group.color} border`}>{group.label}</Badge>
                  <span className="text-sm text-muted-foreground">{group.accounts.length} accounts</span>
                </div>
              </button>
              {expandedTypes.includes(group.value) && (
                <CardContent className="p-0 border-t border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-24">Code</th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Account Name</th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-48">Arabic Name</th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-40">Sub Type</th>
                          <th className="text-right px-4 py-2.5 font-medium text-muted-foreground w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderedTree(group.accounts).map(acc => {
                          const depth = depthByCode[acc.code] || 0;
                          return (
                          <tr key={acc.id} className={`border-t border-stone-50 hover:bg-muted/50 transition-colors ${depth > 0 ? 'bg-stone-25' : ''}`} data-testid={`account-row-${acc.code}`}>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                              <span style={{ paddingLeft: `${depth * 14}px` }} className={depth === 0 ? 'font-semibold' : ''}>
                                {depth > 0 && <CornerDownRight className="w-3 h-3 inline mr-1 text-muted-foreground/60" />}
                                {acc.code}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span style={{ paddingLeft: `${depth * 14}px` }} className={depth === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}>{acc.name}</span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground text-xs" dir="rtl">{acc.name_ar || ''}</td>
                            <td className="px-4 py-3 text-muted-foreground text-xs capitalize">{(acc.sub_type || '').replace(/_/g, ' ')}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => openAddSub(acc)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid={`add-sub-${acc.code}`} title="Add sub-account">
                                  <Plus className="w-3.5 h-3.5 text-primary" />
                                </button>
                                <button onClick={() => openEdit(acc)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid={`edit-${acc.code}`}>
                                  <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                </button>
                                {!acc.is_system && (
                                  <button onClick={() => handleDelete(acc)} className="p-1.5 rounded-lg hover:bg-error/10 transition-colors" data-testid={`delete-${acc.code}`}>
                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Expense Category Tree (sub-sub categories rolled up under each main) */}
        {categoryTree.length > 0 && (filterType === 'all' || filterType === 'expense') && (
          <Card data-testid="expense-category-tree-card">
            <div className="px-4 py-3 bg-warning/10 border-b border-warning/20 flex items-center gap-2">
              <BookOpen size={15} className="text-warning" />
              <h2 className="text-sm font-semibold text-warning">Expense Category Tree</h2>
              <span className="text-[11px] text-muted-foreground ml-2">
                Categories your expenses use, mapped to Chart-of-Accounts entries · sub-sub levels supported
              </span>
              <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => window.location.href = '/category-account-mapping'} data-testid="manage-categories-btn">
                Manage <ChevronRight size={12} />
              </Button>
            </div>
            <CardContent className="p-0">
              <div className="divide-y divide-border/40">
                {categoryTree.map(node => (
                  <div key={node.raw} className="p-3" data-testid={`tree-main-${node.raw}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{node.name}</span>
                        {node.account_name ? (
                          <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
                            → {node.account_code ? `[${node.account_code}] ` : ''}{node.account_name}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">Unmapped</Badge>
                        )}
                        {(node.subcategories || []).length > 0 && (
                          <span className="text-[10px] text-muted-foreground">{node.subcategories.length} sub</span>
                        )}
                      </div>
                    </div>
                    {(node.subcategories || []).length > 0 && (
                      <div className="ml-4 mt-2 space-y-0.5">
                        {node.subcategories.map(sub => (
                          <div key={sub.id} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`tree-sub-${sub.id}`}>
                            <ChevronRight size={11} />
                            <span className="text-foreground">{sub.name}</span>
                            {(sub.subcategories || []).length > 0 && (
                              <span className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
                                {sub.subcategories.length} deeper
                              </span>
                            )}
                            {(sub.subcategories || []).length > 0 && (
                              <div className="ml-3 pl-3 border-l border-border/40 mt-0.5 flex flex-wrap gap-2">
                                {sub.subcategories.map(ss => (
                                  <span key={ss.id} className="text-[10px] text-muted-foreground" data-testid={`tree-subsub-${ss.id}`}>· {ss.name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}



        {/* Add/Edit Account Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent data-testid="account-modal">
            <DialogHeader>
              <DialogTitle>{editAcc ? 'Edit Account' : 'Add Account'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Code *</label>
                  <Input value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                    placeholder="e.g. 041-21" disabled={!!editAcc} data-testid="account-code-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Name *</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Account name" data-testid="account-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Arabic Name</label>
                  <Input value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})}
                    placeholder="الاسم بالعربية" dir="rtl" data-testid="account-name-ar-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Parent Code</label>
                  <div className="flex gap-1.5">
                    <Input value={form.parent_code} onChange={e => setForm({...form, parent_code: e.target.value})}
                      placeholder="e.g. 041" data-testid="account-parent-code-input" />
                    {!editAcc && form.parent_code && (
                      <Button variant="outline" size="sm" type="button" className="shrink-0 text-[10px] h-10"
                        onClick={async () => {
                          try {
                            const res = await api.get('/accounting/accounts/next-code', { params: { parent_code: form.parent_code } });
                            setForm(f => ({ ...f, code: res.data?.next_code || f.code }));
                          } catch { toast.error('Could not fetch next code'); }
                        }}
                        data-testid="autonumber-btn" title="Auto-generate next sub-code">
                        Auto #
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v, sub_type: SUB_TYPES[v]?.[0] || ''})}>
                    <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map(tp => <SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Sub Type</label>
                  <Select value={form.sub_type} onValueChange={v => setForm({...form, sub_type: v})}>
                    <SelectTrigger data-testid="account-subtype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(SUB_TYPES[form.type] || []).map(st => (
                        <SelectItem key={st} value={st}>{st.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Optional description" data-testid="account-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-account-btn">
                {editAcc ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Business Type Setup Wizard */}
        <Dialog open={showSetupWizard} onOpenChange={setShowSetupWizard}>
          <DialogContent className="sm:max-w-xl" data-testid="business-setup-wizard">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-warning" />
                Quick Setup — Choose Your Business Type
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                We'll add the right accounts and expense categories for your industry. You can always customize later.
              </p>
            </DialogHeader>

            {setupDone ? (
              <div className="flex flex-col items-center py-8 gap-3">
                <div className="w-14 h-14 rounded-full bg-success/20 flex items-center justify-center">
                  <Check className="w-7 h-7 text-success" />
                </div>
                <p className="text-lg font-semibold text-foreground">Setup Complete!</p>
                <p className="text-sm text-muted-foreground">Your accounts and categories are ready.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-2 py-2">
                  {businessTypes.map(bt => {
                    const Icon = BUSINESS_TYPE_ICONS[bt.id] || Store;
                    const gradient = BUSINESS_TYPE_COLORS[bt.id] || 'from-gray-500 to-gray-600';
                    const isSelected = selectedBizType === bt.id;
                    return (
                      <button
                        key={bt.id}
                        onClick={() => setSelectedBizType(bt.id)}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected ? 'border-stone-800 bg-muted/50 shadow-sm' : 'border-border hover:border-border hover:bg-muted/50'
                        }`}
                        data-testid={`biz-type-${bt.id}`}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{bt.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{bt.description}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="secondary" className="text-[10px]">+{bt.accounts} accounts</Badge>
                          {isSelected && <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSetupWizard(false)}>Cancel</Button>
                  <Button onClick={handleSetupBizType} disabled={!selectedBizType || settingUp} data-testid="apply-setup-btn">
                    {settingUp ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Setting up...</> : 'Apply Setup'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
